import React, { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

/**
 * SystemBarOverlay - Displays a system banner image above game cover art
 * Now fetches banner configurations from the database.
 */

// Cache for system bar configs
let systemBarCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fallback colors for systems without banner images
const FALLBACK_COLORS = {
  "Xbox": { color: "#107C10", label: "XBOX" },
  "Xbox 360": { color: "#107C10", label: "XBOX 360" },
  "Xbox One": { color: "#107C10", label: "XBOX ONE" },
  "Xbox Series X|S": { color: "#0E7A0D", label: "XBOX SERIES X|S" },
  "PS1": { color: "#003087", label: "PLAYSTATION" },
  "PS2": { color: "#003087", label: "PLAYSTATION 2" },
  "PS3": { color: "#003087", label: "PLAYSTATION 3" },
  "PS4": { color: "#003087", label: "PLAYSTATION 4" },
  "PS5": { color: "#003087", label: "PLAYSTATION 5" },
  "PS Vita": { color: "#003087", label: "PS VITA" },
  "PSP": { color: "#003087", label: "PSP" },
  "PC": { color: "#1B2838", label: "PC" },
  "Steam": { color: "#1B2838", label: "STEAM" },
  "Nintendo Switch": { color: "#E60012", label: "NINTENDO SWITCH" },
  "Wii U": { color: "#009AC7", label: "WII U" },
  "Wii": { color: "#009AC7", label: "WII" },
  "Nintendo 3DS": { color: "#D12228", label: "NINTENDO 3DS" },
  "Epic": { color: "#2F2D2E", label: "EPIC GAMES" },
  "default": { color: "#333333", label: "" }
};

// Platform to default system mapping
const PLATFORM_DEFAULT_SYSTEM = {
  "xbox": "Xbox One",
  "playstation": "PS4",
  "steam": "Steam",
  "epic": "Epic",
  "nintendo": "Nintendo Switch",
  "pc": "PC"
};

/**
 * Fetch system bar configs from database (with caching)
 */
export const fetchSystemBarConfigs = async () => {
  const now = Date.now();
  if (systemBarCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return systemBarCache;
  }
  
  try {
    const response = await axios.get(`${API}/config/system-bars`);
    systemBarCache = response.data.configs || [];
    cacheTimestamp = now;
    return systemBarCache;
  } catch (error) {
    console.error("Failed to fetch system bar configs:", error);
    return systemBarCache || [];
  }
};

/**
 * Clear the cache (call after updating configs)
 */
export const clearSystemBarCache = () => {
  systemBarCache = null;
  cacheTimestamp = 0;
};

/**
 * Get the resolved system name based on platform, system, and override
 */
export const getResolvedSystem = (platform, system, override = null) => {
  if (override) return override;
  if (system) return system;
  if (platform && PLATFORM_DEFAULT_SYSTEM[platform.toLowerCase()]) {
    return PLATFORM_DEFAULT_SYSTEM[platform.toLowerCase()];
  }
  return null;
};

/**
 * Get all available system options for the dropdown
 */
export const getSystemBarOptions = () => {
  // Return from cache if available, otherwise return fallback list
  if (systemBarCache) {
    return systemBarCache.map(config => ({
      value: config.systemName,
      label: config.displayLabel || config.systemName,
      hasBanner: config.versions?.some(v => v.isActive && v.imageUrl),
      color: config.color || "#333333"
    }));
  }
  
  // Fallback to hardcoded list
  return Object.keys(FALLBACK_COLORS)
    .filter(k => k !== "default")
    .map(system => ({
      value: system,
      label: FALLBACK_COLORS[system]?.label || system,
      hasBanner: false,
      color: FALLBACK_COLORS[system]?.color || "#333333"
    }));
};

/**
 * SystemBarOverlay Component
 */
const SystemBarOverlay = ({ 
  platform, 
  system, 
  override = null, 
  show = true,
  className = ""
}) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const resolvedSystem = getResolvedSystem(platform, system, override);
  
  useEffect(() => {
    if (!show || !resolvedSystem) {
      setLoading(false);
      return;
    }
    
    const loadConfig = async () => {
      const configs = await fetchSystemBarConfigs();
      const found = configs.find(c => c.systemName === resolvedSystem);
      setConfig(found);
      setLoading(false);
    };
    
    loadConfig();
  }, [resolvedSystem, show]);
  
  if (!show || !resolvedSystem || loading) return null;
  
  // Get active version's image URL
  const activeVersion = config?.versions?.find(v => v.isActive);
  const bannerUrl = activeVersion?.imageUrl;
  
  // Use config colors or fallback
  const fallback = config 
    ? { color: config.color, label: config.displayLabel, textColor: config.textColor }
    : FALLBACK_COLORS[resolvedSystem] || FALLBACK_COLORS.default;
  
  // If we have a banner image, use it
  if (bannerUrl) {
    return (
      <div 
        className={`w-full overflow-hidden ${className}`}
        style={{ height: "auto" }}
        data-testid="system-bar-overlay"
      >
        <img 
          src={bannerUrl} 
          alt={resolvedSystem}
          className="w-full h-auto object-cover"
          style={{ 
            maxHeight: "40px",
            objectFit: "cover",
            objectPosition: "center"
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
    );
  }
  
  // Fallback to colored bar with text
  if (!fallback.label) return null;
  
  return (
    <div 
      className={`w-full flex items-center justify-center font-bold text-xs tracking-wider ${className}`}
      style={{
        backgroundColor: fallback.color,
        height: "24px",
        color: fallback.textColor || "#FFFFFF",
        textShadow: "0 1px 2px rgba(0,0,0,0.5)"
      }}
      data-testid="system-bar-overlay"
    >
      {fallback.label}
    </div>
  );
};

/**
 * GameCoverWithBar - Wrapper component that combines system bar + cover image
 */
export const GameCoverWithBar = ({ 
  game, 
  className = "",
  imageClassName = "w-full h-full object-contain",
  children,
  showPlaceholder = true 
}) => {
  const showBar = game?.showSystemBar;
  const boxArtUrl = game?.boxArtUrl;
  
  return (
    <div className={`flex flex-col overflow-hidden ${className}`}>
      {showBar && (
        <SystemBarOverlay 
          platform={game?.platforms?.[0]} 
          system={game?.systems?.[0]}
          override={game?.systemBarOverride}
          show={true}
        />
      )}
      {children ? (
        children
      ) : boxArtUrl ? (
        <img 
          src={boxArtUrl} 
          alt={game?.name || "Game cover"}
          className={imageClassName}
          onError={(e) => {
            if (showPlaceholder) {
              e.target.src = "/placeholder-game.png";
            }
          }}
        />
      ) : showPlaceholder ? (
        <div className={`bg-white/10 flex items-center justify-center ${imageClassName}`}>
          <span className="text-white/30 text-xs">No Image</span>
        </div>
      ) : null}
    </div>
  );
};

export default SystemBarOverlay;
